import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const OrderTracking = ({ orderStatus, onNewOrder }) => {
  const [currentStatus, setCurrentStatus] = useState('received');
  const [estimatedTime, setEstimatedTime] = useState(20);

  const statusSteps = [
    { id: 'received', name: 'Order Received', icon: 'CheckCircle', description: 'We got your order!' },
    { id: 'preparing', name: 'Preparing Fresh', icon: 'ChefHat', description: 'Our chefs are working on your meal' },
    { id: 'ready', name: orderStatus.orderType === 'pickup' ? 'Ready for Pickup' : 'Out for Delivery', icon: orderStatus.orderType === 'pickup' ? 'Clock' : 'Truck', description: orderStatus.orderType === 'pickup' ? 'Your order is ready!' : 'On the way to you!' }
  ];

  useEffect(() => {
    const timer1 = setTimeout(() => {
      setCurrentStatus('preparing');
      setEstimatedTime(15);
    }, 3000);

    const timer2 = setTimeout(() => {
      setCurrentStatus('ready');
      setEstimatedTime(0);
    }, 8000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  const getCurrentStepIndex = () => {
    return statusSteps.findIndex(step => step.id === currentStatus);
  };

  return (
    <div className="min-h-screen bg-background pt-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-success-500 to-trust-500 text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircle" size={32} />
          </div>
          <h1 className="font-headline text-4xl font-bold mb-2">Order Confirmed!</h1>
          <p className="text-xl opacity-90">Order #{orderStatus.id}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Order Tracking */}
          <div className="lg:col-span-2 space-y-8">
            {/* Status Timeline */}
            <div className="card-surface">
              <h2 className="font-headline text-2xl font-bold text-text-primary mb-6">Order Status</h2>
              
              <div className="space-y-6">
                {statusSteps.map((step, index) => {
                  const isCompleted = index <= getCurrentStepIndex();
                  const isCurrent = index === getCurrentStepIndex();
                  
                  return (
                    <div key={step.id} className="flex items-start space-x-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-organic ${
                        isCompleted 
                          ? 'bg-success text-white' :'bg-gray-200 text-gray-400'
                      }`}>
                        <Icon name={step.icon} size={20} />
                      </div>
                      <div className="flex-1">
                        <h3 className={`font-semibold ${
                          isCompleted ? 'text-text-primary' : 'text-text-secondary'
                        }`}>
                          {step.name}
                        </h3>
                        <p className={`text-sm ${
                          isCompleted ? 'text-text-secondary' : 'text-gray-400'
                        }`}>
                          {step.description}
                        </p>
                        {isCurrent && estimatedTime > 0 && (
                          <div className="mt-2 flex items-center space-x-2 text-primary">
                            <Icon name="Clock" size={16} />
                            <span className="text-sm font-medium">
                              Estimated {estimatedTime} minutes remaining
                            </span>
                          </div>
                        )}
                      </div>
                      {isCompleted && (
                        <div className="text-success">
                          <Icon name="Check" size={20} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {currentStatus === 'ready' && (
                <div className="mt-6 p-4 bg-success-50 border border-success-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Icon name="CheckCircle" size={24} className="text-success" />
                    <div>
                      <h4 className="font-semibold text-success-800">
                        {orderStatus.orderType === 'pickup' ? 'Ready for Pickup!' : 'Delivered!'}
                      </h4>
                      <p className="text-success-600 text-sm">
                        {orderStatus.orderType === 'pickup' ?'Your order is ready at the restaurant' :'Your order has been delivered'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Order Details */}
            <div className="card-surface">
              <h3 className="font-semibold text-text-primary mb-4">Order Details</h3>
              <div className="space-y-3">
                {orderStatus.items.map((item) => (
                  <div key={item.cartId} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                    <div className="flex-1">
                      <h4 className="font-medium text-text-primary">{item.name}</h4>
                      {item.customizations && (
                        <div className="text-sm text-text-secondary">
                          {item.customizations.spiceLevel && `Spice: ${item.customizations.spiceLevel}/5 • `}
                          {item.customizations.sides && `${item.customizations.sides} • `}
                          Qty: {item.quantity}
                        </div>
                      )}
                    </div>
                    <span className="font-medium text-primary">
                      ${(item.price * item.quantity).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between font-semibold">
                <span className="text-text-primary">Total</span>
                <span className="text-primary">${orderStatus.total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Restaurant Info */}
            <div className="card-surface">
              <h3 className="font-semibold text-text-primary mb-4">
                {orderStatus.orderType === 'pickup' ? 'Pickup Location' : 'Restaurant'}
              </h3>
              <div className="space-y-3">
                <h4 className="font-medium text-text-primary">{orderStatus.location.name}</h4>
                <p className="text-text-secondary text-sm">{orderStatus.location.address}</p>
                <div className="flex items-center space-x-2 text-sm">
                  <Icon name="Phone" size={16} className="text-primary" />
                  <span className="text-text-primary">{orderStatus.location.phone}</span>
                </div>
                {orderStatus.orderType === 'pickup' && (
                  <div className="mt-4 p-3 bg-primary-50 rounded-lg">
                    <p className="text-primary text-sm font-medium">
                      Please bring your order confirmation when picking up
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              <button
                onClick={onNewOrder}
                className="w-full btn-primary"
              >
                Place New Order
              </button>
              <Link
                to="/interactive-menu-culinary-discovery"
                className="w-full btn-conversion block text-center"
              >
                Browse Menu
              </Link>
            </div>

            {/* Support */}
            <div className="card-surface">
              <h3 className="font-semibold text-text-primary mb-4">Need Help?</h3>
              <div className="space-y-3">
                <button className="w-full flex items-center justify-center space-x-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-organic">
                  <Icon name="Phone" size={18} />
                  <span>Call Restaurant</span>
                </button>
                <button className="w-full flex items-center justify-center space-x-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-organic">
                  <Icon name="MessageCircle" size={18} />
                  <span>Live Chat</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTracking;